import { Mark } from '@tiptap/core';

/**
 * LawCitationMark — TipTap Mark for durable anchors around extracted law
 * citations. Stores citationId + doc metadata directly in the document.
 *
 * The mark is round-tripped through HTML via ``<span data-citation-id="...">``.
 * Yjs preserves marks across edits: users can rewrite surrounding text and
 * the mark stays attached to the citation phrase. The regulatory-alert flow
 * uses the citationId as an O(1) anchor — no string search, no locale logic.
 */
const LawCitationMark = Mark.create({
  name: 'lawCitation',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      citationId: {
        default: null,
        parseHTML: el => el.getAttribute('data-citation-id'),
        renderHTML: attrs =>
          attrs.citationId ? { 'data-citation-id': attrs.citationId } : {},
      },
      docNumber: {
        default: null,
        parseHTML: el => el.getAttribute('data-doc-number'),
        renderHTML: attrs =>
          attrs.docNumber ? { 'data-doc-number': attrs.docNumber } : {},
      },
      docType: {
        default: null,
        parseHTML: el => el.getAttribute('data-doc-type'),
        renderHTML: attrs =>
          attrs.docType ? { 'data-doc-type': attrs.docType } : {},
      },
      articleRef: {
        default: null,
        parseHTML: el => el.getAttribute('data-article-ref'),
        renderHTML: attrs =>
          attrs.articleRef ? { 'data-article-ref': attrs.articleRef } : {},
      },
      flagged: {
        // Runtime state: true when an active regulatory alert targets this
        // citation. Rendered as a class so the highlight shows in the editor.
        default: false,
        parseHTML: el => el.classList?.contains('law-citation-flagged') || false,
        renderHTML: attrs => (
          attrs.flagged
            ? { class: 'law-citation law-citation-flagged' }
            : { class: 'law-citation' }
        ),
      },
    };
  },

  // Don't extend the mark when typing at the boundary — prevents accidentally
  // marking user edits before/after the citation.
  inclusive: false,

  parseHTML() {
    return [{ tag: 'span[data-citation-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    // Merge: if the flagged attribute already set a class, respect it;
    // otherwise default to plain ``law-citation``.
    const cls = HTMLAttributes.class || 'law-citation';
    return ['span', { ...HTMLAttributes, class: cls }, 0];
  },

  addCommands() {
    return {
      /**
       * Flag (or unflag) every citation whose citationId is in the given list.
       * Used when an alert opens — scrolls to the first flagged citation.
       */
      flagLawCitations:
        (citationIds) =>
        ({ tr, state, dispatch }) => {
          const markType = state.schema.marks.lawCitation;
          if (!markType) return false;

          const ids = new Set(citationIds || []);
          let changed = false;

          state.doc.descendants((node, pos) => {
            if (!node.isText) return;
            node.marks.forEach(mark => {
              if (mark.type !== markType) return;
              const shouldFlag = ids.has(mark.attrs.citationId);
              if (shouldFlag !== (mark.attrs.flagged === true)) {
                tr.removeMark(pos, pos + node.nodeSize, markType);
                tr.addMark(
                  pos,
                  pos + node.nodeSize,
                  markType.create({ ...mark.attrs, flagged: shouldFlag }),
                );
                changed = true;
              }
            });
          });

          if (changed && dispatch) {
            dispatch(tr);
          }
          // Return true even when unchanged — this is idempotent.
          return true;
        },

      /**
       * Clear every flagged state. Called when the alert banner is dismissed
       * or the URL ``alertId`` param is removed.
       */
      clearLawCitationFlags:
        () =>
        ({ tr, state, dispatch }) => {
          const markType = state.schema.marks.lawCitation;
          if (!markType) return false;
          let changed = false;
          state.doc.descendants((node, pos) => {
            if (!node.isText) return;
            node.marks.forEach(mark => {
              if (mark.type === markType && mark.attrs.flagged) {
                tr.removeMark(pos, pos + node.nodeSize, markType);
                tr.addMark(
                  pos,
                  pos + node.nodeSize,
                  markType.create({ ...mark.attrs, flagged: false }),
                );
                changed = true;
              }
            });
          });
          if (changed && dispatch) dispatch(tr);
          return true;
        },

      /**
       * Wrap the current selection with a new citationId. Used by the manual
       * anchor flow: after a ``manual_required`` resolution, the user clicks
       * the clause, the frontend generates a UUID, wraps the selection, and
       * POSTs it back so the dependency row is persisted.
       */
      addLawCitationMark:
        ({ citationId, docNumber, docType, articleRef }) =>
        ({ commands }) => {
          return commands.setMark(this.name, {
            citationId,
            docNumber: docNumber || null,
            docType: docType || null,
            articleRef: articleRef || null,
            flagged: true,
          });
        },
    };
  },
});

export default LawCitationMark;
